import { getFormSyncErrors } from 'redux-form';
import { invert, has, get } from 'lodash';
import moment from 'moment';

import { ApplicationFieldEnum as Fields } from 'Common/FieldEnum';
import { ActiveStatusEnum } from 'Common/Constants/Enumeration/ActiveStatusEnum';
import { FrequencyEnum } from 'Common/Constants/Enumeration/FrequencyEnum';
import { orderMembersByType } from '../Helpers';
import { getAllMembersWithIncome } from '../HouseholdExceptionHelpers';
import {
  APPLICATION_HUB_CONTACT_INFO_FORM_NAME,
  APPLICATION_HUB_APP_INFO_FORM_NAME,
} from 'Common/FormEnum';
import { StatusChangeTypeEnum as StatusChange } from 'Common/Constants/Enumeration/StatusChangeTypeEnum';
import { MonthEnum } from 'Common/Constants/Enumeration/MonthEnum';
import { ChangeEffectiveDateScenarioEnum as Scenario } from 'Common/Constants/Enumeration/ChangeEffectiveDateScenarioEnum';
import { DATE_FORMAT_FOR_DB_STRING } from 'Common/Constants';

import { SecondReviewResultEnumeration } from '../Constants.js';

/**
 * Computes props required for the Application Hub based on
 * the passed in `state` object (looking primarily at the
 * `tempApplication` object) and passed in `ownProps` object.
 * @param {Object} state The full state object, necessary for
 * redux-form selectors
 * @param {Object} ownProps The `ownProps` object that is the
 * second parameter in `mapStateToProps`
 * @returns {Object} The list of computed props required for
 * the Application Hub:
 *  - `applicationDate`
 *  - `appInfoErrors`
 *  - `appInfoInitialValues`
 *  - `contactInfoErrors`
 *  - `familySize`
 *  - `hadNote`
 *  - `hasHOH`
 *  - `hubHeaderInitialValues`
 *  - `incomeData`
 *  - `incomeString`
 *  - `incomeSummary`
 *  - `isNewApp`
 *  - `memberData`
 *  - `releaseMapping`
 */
export const computedProps = (state, ownProps) => {
  const {
    ManageApplicationState: { hub },
  } = state;
  const { ApplicationInformation: appInfo, Members } = hub.tempApplication;
  return {
    applicationDate: appInfo ? appInfo[Fields.ApplicationDate] : null,
    appInfoErrors: getAppInfoErrors(state),
    appInfoInitialValues: getAppInformationInitialValues(appInfo),
    contactInfoErrors: getContactInfoErrors(state),
    familySize: appInfo ? appInfo.FamilySize : 0,
    hadNote: hadNote(hub.dbApplication),
    hasHOH: hasHOH(hub.tempApplication.Members),
    hasStudents: hasStudents(hub.tempApplication.Students),
    hubHeaderInitialValues: getHubHeaderInitialValues(appInfo, Members),
    incomeData: getIncomeData(hub.tempApplication),
    incomeString: getIncomeString(appInfo),
    incomeSummary: getIncomeSummary(appInfo),
    isNewApp: ownProps.match.params.familyId === '0',
    memberData: getMemberData(hub.tempApplication),
    releaseMapping: appInfo ? appInfo[Fields.ReleaseMapping] : [],
  };
};

/**
 * Gets the relevant fields for the Hub Header from the provided
 * Application Information object, `appInfo`. The following fields
 * are included: `ApplicationName`, `FamilyNumber`, `ApplicationDate`,
 * `IsDeclinedBenefits`, `IsReducedBenefits`, `IsIncomplete` and
 * `ActiveStatus`
 * @param {Object} appInfo
 * @param {Array} members
 * @returns {Object} The `initialValues` object for the
 * Hub Header form.
 */
const getHubHeaderInitialValues = (appInfo, members = []) => {
  if (!appInfo) {
    return {};
  }

  const hoh = members.find((x) => x.IsHOH);
  const applicationName = hoh ? `${hoh[Fields.LastName]} Household` : 'TBD';

  return {
    [Fields.ApplicationName]: applicationName,
    [Fields.FamilyNumber]: appInfo[Fields.FamilyNumber],
    [Fields.ApplicationDate]: appInfo[Fields.ApplicationDate],
    [Fields.IsDeclinedBenefits]: appInfo[Fields.IsDeclinedBenefits],
    [Fields.IsReducedBenefits]: appInfo[Fields.IsReducedBenefits],
    [Fields.IsIncomplete]: appInfo[Fields.IsIncomplete],
    [Fields.ActiveStatus]: invert(ActiveStatusEnum)[
      appInfo[Fields.ActiveStatus]
    ],
    [Fields.SecondReviewMarkAsNoChange]:
      appInfo.SecondReviewResultId === SecondReviewResultEnumeration.NoChange,
    [Fields.SecondReviewMarkAsIncomplete]:
      appInfo.SecondReviewResultId ===
      SecondReviewResultEnumeration.IncompleteApplicationError,
  };
};

/**
 * Gets the relevant fields for the Application Information form
 * from the provided Application Information object, `appInfo`.
 * The following fields are included: `Notes`, `ReleaseMapping`,
 * `VerificationStatus` and `FamilySize`
 * @param {Object} appInfo
 * @returns {Object} The `initialValues` object for the
 * Application Information form.
 */
const getAppInformationInitialValues = (appInfo) => {
  if (!appInfo) {
    return {};
  }

  const notes =
    appInfo[Fields.Notes] && appInfo[Fields.Notes].length > 0
      ? appInfo[Fields.Notes][0].Description
      : null;

  return {
    [Fields.Notes]: notes,
    [Fields.ReleaseMapping]: appInfo[Fields.ReleaseMapping] || [],
    [Fields.VerificationStatus]: appInfo[Fields.VerificationStatus],
    [Fields.FamilySize]: appInfo[Fields.FamilySize],
  };
};

/**
 * Determines if the passed in Application Information object
 * `appInfo` has an Income Summary and returns it if so.
 * Otherwise returns a default Income Summary object.
 * @param {Object} appInfo
 * @returns {Object} An Income Summary object
 */
const getIncomeSummary = (appInfo) => {
  return has(appInfo, 'IncomeSummary')
    ? appInfo.IncomeSummary
    : {
        Frequency: -1,
        TotalIncome: 0,
      };
};

/**
 * Generates the string for displaying the summarized
 * income of the application.
 * @param {Object} appInfo
 * @returns {String} A string summarizing the
 * income on the provided Application Information
 * object, `appInfo`
 */
const getIncomeString = (appInfo) => {
  let totalIncome = '';
  let freq = '';

  const summary = getIncomeSummary(appInfo);

  if (summary === null) {
    return 'No income data found.';
  }

  totalIncome = summary.TotalIncome.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  freq = invert(FrequencyEnum)[summary.Frequency];

  if (freq) {
    freq = freq.replace(/_/g, ' ');
  }

  return `${totalIncome} ${freq}`;
};

/**
 * Determines whether any of the members in the passed
 * in `Members` collection are flagged as Head of
 * Household, as indicated by the `IsHOH` property.
 * @param {Array} Members
 * @returns {Boolean} Whether or not the passed in
 * collection (`Members`) contains a member that is
 * flagged as Head of Household.
 */
const hasHOH = (Members) => {
  if (!Members) {
    return false;
  }

  return Members.find((member) => member[Fields.IsHOH]) !== undefined;
};

/**
 * Determines whether the passed in array contains
 * any students.
 * @param {Array} Students
 * @returns {Boolean} Whether or not the passed in
 * collection (`Students`) contains any students
 */
const hasStudents = (Students) => {
  return Students === undefined ? false : Students.length !== 0;
};

/**
 * Combines and sorts the `Members` and `Students` for
 * display in the Household Members table
 * @param {Object} param0 An object with `Members`
 * and `Students` properties
 * @returns {Array} A combined and ordered array
 * of the passed in `Members` and `Students`.
 */
const getMemberData = ({ Members, Students }) => {
  return Members ? orderMembersByType([...Members, ...Students]) : [];
};

/**
 * Combines and sorts the `Members` and `Students` for
 * display in the Member Income table
 * @param {Object} param0 An object with `Members`
 * and `Students` properties
 * @returns {Array} A combined and ordered array
 * of the passed in `Members` and `Students` that
 * have some income.
 */
const getIncomeData = ({ Members, Students }) => {
  const data = getAllMembersWithIncome(Students, Members);
  return orderMembersByType(data);
};

/**
 * Determines if the passed in original application
 * had data in the Notes field.
 * @param {Object} originalApplication
 * @returns {Boolean}
 */
const hadNote = (originalApplication) => {
  return (
    originalApplication &&
    originalApplication.ApplicationInformation &&
    originalApplication.ApplicationInformation[Fields.Notes] &&
    originalApplication.ApplicationInformation[Fields.Notes].length > 0
  );
};

/**
 * Uses the redux-form `getFormSyncErrors()` selector to
 * get the sync errors for the Contact Info Form
 * @param {Object} state The full state object
 * @returns {Object} The redux-form sync errors for the
 * Contact Info Form
 */
const getContactInfoErrors = (state) => {
  return getFormSyncErrors(APPLICATION_HUB_CONTACT_INFO_FORM_NAME)(state);
};

/**
 * Uses the redux-form `getFormSyncErrors()` selector to
 * get the sync errors for the Application Information form
 * @param {Object} state The full state object
 * @returns {Object} The redux-form sync errors for the
 * Application Information Form
 */
const getAppInfoErrors = (state) => {
  return getFormSyncErrors(APPLICATION_HUB_APP_INFO_FORM_NAME)(state);
};

/**
 * A no-op submit to fulfill the requirement of having an
 * `onSubmit` handler on redux-forms. Used when the data
 * is updated in state outside of a form submit.
 */
export const noopSubmit = () => {};

/**
 * Determines what change effective date scenario applies
 * based on the given `student`, the user's ability to
 * `canSetPastEffectiveDate` and whether or not it `isNewApp`.
 * @param {Object} param0 An object with `student`, `isNewApp`
 * and `canSetPastEffectiveDate` properties
 */
export const getChangeEffectiveDateScenario = ({
  student,
  canSetPastEffectiveDate,
  isNewApp,
}) => {
  const isNewStudent = student[Fields.IdSeed] === 0;
  const isNewToApp = student[Fields.IsNewToApp];

  switch (student[Fields.StatusChangeType]) {
    case StatusChange.None:
      if (canSetPastEffectiveDate) {
        if (isNewStudent) {
          if (isNewApp) {
            return Scenario.NoStatusChange_CanSetPastEffectiveDate_NewStudent_AddedToNewApp;
          } else {
            return Scenario.NoStatusChange_CanSetPastEffectiveDate_NewStudent_AddedToExistingApp;
          }
        } else {
          if (isNewApp) {
            return Scenario.NoStatusChange_CanSetPastEffectiveDate_ExistingStudent_AddedToNewApp;
          } else {
            if (isNewToApp) {
              return Scenario.NoStatusChange_CanSetPastEffectiveDate_ExistingStudent_AddedToExistingApp;
            } else {
              return Scenario.NoStatusChange_CanSetPastEffectiveDate_ExistingStudent_OnExistingApp;
            }
          }
        }
      } else {
        if (isNewStudent) {
          if (isNewApp) {
            return Scenario.NoStatusChange_NewStudent_AddedToNewApp;
          } else {
            return Scenario.NoStatusChange_NewStudent_AddedToExistingApp;
          }
        } else {
          if (isNewApp) {
            return Scenario.NoStatusChange_ExistingStudent_AddedToNewApp;
          } else {
            if (isNewToApp) {
              return Scenario.NoStatusChange_ExistingStudent_AddedToExistingApp;
            } else {
              return Scenario.NoStatusChange_ExistingStudent_OnExistingApp;
            }
          }
        }
      }

    case StatusChange.Beneficial:
      if (canSetPastEffectiveDate) {
        return Scenario.BeneficialStatusChange_CanSetPastEffectiveDate;
      } else {
        return Scenario.BeneficialStatusChange;
      }

    case StatusChange.Adverse:
      if (canSetPastEffectiveDate) {
        return Scenario.AdverseStatusChange_CanSetPastEffectiveDate;
      } else {
        return Scenario.AdverseStatusChange;
      }

    default:
      return Scenario.BeneficialStatusChange;
  }
};

/**
 * Gets the `datePickerProps` for the `DateDialog` used
 * to change the Effective Date of students. Depending on
 * the scenario (determined by `getChangeEffectiveDateScenario()`),
 * the returned object will have either a `filterDate` or
 * `includeDates` property defining valid Effective Dates
 * @param {Object} An object with `student`, `isNewApp`,
 * `applicationDate`, `canSetPastEffectiveDate`, `fiscalYear`
 * and `currentEffectiveDate` properties
 * @returns {Object} `datePickerProps`
 */
export const getEffectiveDatePickerProps = ({
  student,
  isNewApp,
  applicationDate,
  canSetPastEffectiveDate,
  fiscalYear,
  currentEffectiveDate,
}) => {
  if (!student) {
    return {};
  }

  const today = moment().startOf('day');
  const appDate = moment(applicationDate);
  const endOfFy = moment({ year: fiscalYear, month: MonthEnum.June, day: 30 });
  const mCurrentEffectiveDate = moment(currentEffectiveDate);

  const scenario = getChangeEffectiveDateScenario({
    student,
    canSetPastEffectiveDate,
    isNewApp,
  });

  switch (scenario) {
    case Scenario.NoStatusChange_CanSetPastEffectiveDate_ExistingStudent_AddedToNewApp:
    case Scenario.NoStatusChange_CanSetPastEffectiveDate_ExistingStudent_AddedToExistingApp:
    case Scenario.NoStatusChange_CanSetPastEffectiveDate_NewStudent_AddedToExistingApp:
    case Scenario.NoStatusChange_CanSetPastEffectiveDate_NewStudent_AddedToNewApp:
    case Scenario.BeneficialStatusChange_CanSetPastEffectiveDate:
      return {
        filterDate: (date) => {
          var momentDate = moment(date);
          return (
            momentDate.isSameOrAfter(appDate) &&
            momentDate.isSameOrBefore(today)
          );
        },
      };

    case Scenario.NoStatusChange_CanSetPastEffectiveDate_ExistingStudent_OnExistingApp:
      //special case to prevent selecting a previous future effective date if the student
      //now no longer has a status future
      if (
        student[Fields.StatusFuture] ||
        mCurrentEffectiveDate.isSameOrBefore(today)
      ) {
        return {
          filterDate: (date) => {
            var momentDate = moment(date);
            return (
              momentDate.isSame(currentEffectiveDate) ||
              (momentDate.isSameOrAfter(appDate) &&
                momentDate.isSameOrBefore(today))
            );
          },
        };
      } else {
        return {
          filterDate: (date) => {
            var momentDate = moment(date);
            return (
              momentDate.isSameOrAfter(appDate) &&
              momentDate.isSameOrBefore(today)
            );
          },
        };
      }

    case Scenario.NoStatusChange_ExistingStudent_AddedToNewApp:
    case Scenario.NoStatusChange_ExistingStudent_AddedToExistingApp:
    case Scenario.NoStatusChange_NewStudent_AddedToExistingApp:
    case Scenario.NoStatusChange_NewStudent_AddedToNewApp:
    case Scenario.BeneficialStatusChange:
      return {
        includeDates: [today],
      };

    case Scenario.NoStatusChange_ExistingStudent_OnExistingApp:
      //special case to prevent selecting a previous future effective date if the student
      //now no longer has a status future
      if (
        student[Fields.StatusFuture] ||
        mCurrentEffectiveDate.isSameOrBefore(today)
      ) {
        return {
          includeDates: [currentEffectiveDate, today],
        };
      } else {
        return {
          includeDates: [today],
        };
      }

    case Scenario.AdverseStatusChange_CanSetPastEffectiveDate:
      return {
        filterDate: (date) => {
          var momentDate = moment(date);
          return (
            momentDate.isSameOrAfter(appDate) &&
            momentDate.isSameOrBefore(endOfFy)
          );
        },
      };

    case Scenario.AdverseStatusChange:
      return {
        filterDate: (date) => {
          var momentDate = moment(date);
          return (
            momentDate.isSameOrAfter(today) &&
            momentDate.isSameOrBefore(endOfFy)
          );
        },
      };

    default:
      return {};
  }
};

export const extractReprocessMealsModel = (application) => {
  const students = get(application, 'Students', []);
  const studentIdSeeds = students.map((s) => s.IdSeed);
  const fromDate = get(application, 'ApplicationInformation.ApplicationDate');

  return {
    FromDate: moment(fromDate).format(DATE_FORMAT_FOR_DB_STRING),
    ToDate: moment().format(DATE_FORMAT_FOR_DB_STRING),
    StudentIdSeeds: studentIdSeeds,
  };
};

/**
 * Determines whether the student's status change is beneficial, neutral, or adverse
 * @param {Number} prevStatus
 * @param {Number} currentStatus
 * @returns {Number}
 */
export const classifyStatusChange = (prevStatus, currentStatus) => {
  const pStatus = Number(prevStatus);
  const cStatus = Number(currentStatus);

  if (pStatus > cStatus) {
    return StatusChange.Beneficial;
  } else if (pStatus < cStatus) {
    return StatusChange.Adverse;
  } else {
    return StatusChange.None;
  }
};

export const formatFrequency = (freqId, amount) => {
  const frequencies = Object.keys(FrequencyEnum);

  return amount > 0 && frequencies[freqId]
    ? amount + ' ' + frequencies[freqId].replace(new RegExp('_', 'g'), ' ')
    : null;
};

export const PAGE_REFS = {
  ReviewIncomingApplication: 'ria',
};

export const QUERY_PARAMS = {
  Ref: 'ref',
  IncomingAppId: 'incomingAppId',
};



// WEBPACK FOOTER //
// ./src/pages/manageapplication/helpers/hub/index.js